import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, MessageCircle, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import { Overline } from "@/components/landing/primitives";

const AutotunerFlex = () => {
  const { t } = useLang();
  const p = t.partner;

  return (
    <section
      id="partner"
      className="relative overflow-hidden bg-[#0A0A0A] px-5 py-24 md:px-10 md:py-32"
      data-testid="partner-section"
    >
      <div className="mx-auto grid max-w-[1400px] items-center gap-12 lg:grid-cols-2 lg:gap-20">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative aspect-[4/5] overflow-hidden border border-white/10 lg:order-1"
        >
          <img
            src="https://images.pexels.com/photos/3807517/pexels-photo-3807517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Autószerelő műhelyben chiptuning partner"
            className="h-full w-full object-cover grayscale transition-all duration-700 hover:grayscale-0"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 to-transparent" />
          <div className="absolute bottom-6 left-6 border-l-2 border-brand-red pl-4">
            <div className="font-mono text-[11px] uppercase tracking-widest text-brand-green">
              Autotuner Flex / Slave
            </div>
            <div className="font-display text-2xl font-black text-white">B2B Network</div>
          </div>
        </motion.div>

        {/* Content */}
        <div className="lg:order-2">
          <Overline color="red">{p.overline}</Overline>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-5 font-display text-5xl font-black leading-[0.95] text-white md:text-7xl"
          >
            {p.title}
          </motion.h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-white/60 md:text-lg">
            {p.subtitle}
          </p>

          <ul className="mt-8 space-y-4">
            {p.points.map((point, i) => (
              <motion.li
                key={point}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex items-start gap-3"
                data-testid="partner-benefit"
              >
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center border border-brand-green/40 bg-brand-green/10">
                  <Check className="h-3.5 w-3.5 text-brand-green" />
                </span>
                <span className="text-sm text-white/80 md:text-base">{point}</span>
              </motion.li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-brand-green px-8 py-4 font-mono text-xs font-medium uppercase tracking-widest text-black transition-transform hover:-translate-y-1 hover:shadow-glow-green"
              data-testid="partner-cta"
            >
              <MessageCircle className="h-4 w-4" />
              {p.cta}
            </a>
            <Link
              to="/autotuner-flex-partner"
              className="inline-flex items-center gap-2 border border-white/20 px-8 py-4 font-mono text-xs font-medium uppercase tracking-widest text-white transition-colors hover:border-brand-green hover:text-brand-green"
              data-testid="partner-more-link"
            >
              {p.more}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AutotunerFlex;
